'use client';

import { useState, useCallback, useMemo, useEffect } from 'react';
import { Calendar, ChevronDown, Clock } from 'lucide-react';
import { format, subDays, startOfDay, endOfDay } from 'date-fns';

import { Button } from '@/components/ui/button';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@/components/ui/popover';
import * as SelectPrimitive from '@radix-ui/react-select';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';

export type DateRange = { from: Date; to: Date };
export type Granularity = 'daily' | 'weekly' | 'monthly';

// Mirrors the backend cap in `src/app/api/crm/trpc/services/analytics/analytics.types.ts`
// (`MAX_ANALYTICS_RANGE_DAYS`, default 750). Kept in sync manually — bumping the env
// override on the server means bumping this constant too.
export const MAX_ANALYTICS_RANGE_DAYS = 750;

// The preset / calendar boundary uses one fewer day because we send
// `startOfDay(from) → endOfDay(to)`, which inflates the raw span by ~24h and would
// otherwise cross the server's `> 750d` reject threshold on the boundary pick.
const PRESET_MAX_DAYS = MAX_ANALYTICS_RANGE_DAYS - 1;

interface FunnelAnalyticsFiltersProps {
    dateRange: DateRange;
    onDateRangeChange: (range: DateRange) => void;
    granularity: Granularity;
    onGranularityChange: (granularity: Granularity) => void;
    /** Show granularity selector (hide for sessions tab) */
    showGranularity?: boolean;
    className?: string;
}

const DATE_PRESETS_KEYS = [
    { key: 'analytics.filters.today', days: 1 },
    { key: 'analytics.filters.last7Days', days: 7 },
    { key: 'analytics.filters.last14Days', days: 14 },
    { key: 'analytics.filters.last30Days', days: 30 },
    { key: 'analytics.filters.last90Days', days: 90 },
    { key: 'analytics.filters.last749Days', days: PRESET_MAX_DAYS },
];

function rangeDays(range: DateRange): number {
    return Math.ceil((range.to.getTime() - range.from.getTime()) / (1000 * 60 * 60 * 24));
}

/** Granularities that make sense for a given span (daily always; weekly >14d; monthly >40d). */
function granularitiesForRange(range: DateRange): Granularity[] {
    const days = rangeDays(range);
    const list: Granularity[] = ['daily'];
    if (days > 14) list.push('weekly');
    if (days > 40) list.push('monthly');
    return list;
}

/**
 * Sensible default granularity for a span so the chart never shows a single
 * flat bar (too coarse) or hundreds of unreadable points (too fine).
 */
function bestGranularity(range: DateRange): Granularity {
    const days = rangeDays(range);
    if (days <= 16) return 'daily';
    if (days <= 92) return 'weekly';
    return 'monthly';
}

/**
 * Unified date + granularity control for funnel analytics.
 * - Date range picker with presets and a two-month calendar
 * - Partial selection is shown live; the range is only applied on "Apply"
 * - Granularity is kept consistent with the selected span (daily/weekly/monthly)
 */
export function FunnelAnalyticsFilters({
    dateRange,
    onDateRangeChange,
    granularity,
    onGranularityChange,
    showGranularity = true,
    className,
}: FunnelAnalyticsFiltersProps) {
    const { t } = useTranslation('storefront');
    const [calendarOpen, setCalendarOpen] = useState(false);

    // Draft selection inside the popover. `to` may be undefined while the user
    // is mid-selection (picked a start but not yet an end).
    const [draft, setDraft] = useState<{ from?: Date; to?: Date }>({
        from: dateRange.from,
        to: dateRange.to,
    });

    // Reset the draft to the committed range whenever the popover opens.
    useEffect(() => {
        if (calendarOpen) {
            setDraft({ from: dateRange.from, to: dateRange.to });
        }
    }, [calendarOpen, dateRange.from, dateRange.to]);

    const handlePresetClick = useCallback((days: number) => {
        const to = endOfDay(new Date());
        const from = days === 1
            ? startOfDay(new Date())
            : startOfDay(subDays(new Date(), days));
        const range = { from, to };
        onDateRangeChange(range);
        onGranularityChange(bestGranularity(range));
        setCalendarOpen(false);
    }, [onDateRangeChange, onGranularityChange]);

    const handleApply = useCallback(() => {
        if (draft.from && draft.to) {
            const range = {
                from: startOfDay(draft.from),
                to: endOfDay(draft.to),
            };
            onDateRangeChange(range);
            onGranularityChange(bestGranularity(range));
            setCalendarOpen(false);
        }
    }, [draft, onDateRangeChange, onGranularityChange]);

    const availableGranularities = useMemo(() => granularitiesForRange(dateRange), [dateRange]);

    // Keep granularity valid for the current span. If the range shrinks so the
    // current granularity is no longer offered (e.g. "Monthly" then "Today"),
    // fall back to the finest available bucket instead of showing an empty select.
    useEffect(() => {
        if (!availableGranularities.includes(granularity)) {
            onGranularityChange(availableGranularities[availableGranularities.length - 1]);
        }
    }, [availableGranularities, granularity, onGranularityChange]);

    const GRANULARITY_LABELS: Record<Granularity, string> = useMemo(() => ({
        daily: t('analytics.filters.daily'),
        weekly: t('analytics.filters.weekly'),
        monthly: t('analytics.filters.monthly'),
    }), [t]);

    const shortDateLabel = useMemo(() => {
        const now = new Date();
        const fromStart = startOfDay(dateRange.from).getTime();
        const todayStart = startOfDay(now).getTime();
        if (fromStart === todayStart) {
            return t('analytics.filters.today');
        }
        for (const preset of DATE_PRESETS_KEYS) {
            if (preset.days > 1) {
                const presetFrom = startOfDay(subDays(now, preset.days)).getTime();
                if (fromStart === presetFrom) return t(preset.key);
            }
        }
        return `${format(dateRange.from, 'MMM d')} – ${format(dateRange.to, 'MMM d')}`;
    }, [dateRange, t]);

    const draftLabel = useMemo(() => {
        if (draft.from && draft.to) {
            return `${format(draft.from, 'MMM d')} – ${format(draft.to, 'MMM d')}`;
        }
        if (draft.from) {
            return t('analytics.filters.pickEndDate');
        }
        return t('analytics.filters.pickStartDate');
    }, [draft, t]);

    return (
        <div className={cn('flex items-center gap-1.5', className)}>
            {/* Date Range Picker */}
            <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                <PopoverTrigger asChild>
                    <Button
                        variant="outline"
                        size="sm"
                        className="h-8 px-2.5 text-[11px] font-semibold"
                    >
                        <Calendar className="mr-1.5 h-3 w-3 text-muted-foreground" />
                        {shortDateLabel}
                        <ChevronDown className="ml-1 h-3 w-3 opacity-50" />
                    </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="end" side="bottom">
                    <div className="flex">
                        {/* Presets */}
                        <div className="border-r p-2 flex flex-col gap-0.5 min-w-[110px]">
                            <p className="text-[10px] font-medium text-muted-foreground mb-1 px-2">{t('analytics.filters.quickSelect')}</p>
                            {DATE_PRESETS_KEYS.map((preset) => (
                                <Button
                                    key={preset.days}
                                    variant="ghost"
                                    size="sm"
                                    className="h-7 px-2 text-xs justify-start font-normal"
                                    onClick={() => handlePresetClick(preset.days)}
                                >
                                    {t(preset.key)}
                                </Button>
                            ))}
                        </div>
                        {/* Calendar + apply footer */}
                        <div className="flex flex-col">
                            <CalendarComponent
                                mode="range"
                                selected={{ from: draft.from, to: draft.to }}
                                onSelect={(range) => {
                                    setDraft({ from: range?.from, to: range?.to });
                                }}
                                numberOfMonths={2}
                                disabled={[
                                    { after: new Date() },
                                    { before: subDays(new Date(), PRESET_MAX_DAYS) },
                                ]}
                                defaultMonth={dateRange.from}
                                className="text-xs p-2"
                            />
                            <div className="flex items-center justify-between gap-2 border-t px-3 py-2">
                                <span className="text-[11px] text-muted-foreground tabular-nums">
                                    {draftLabel}
                                </span>
                                <div className="flex items-center gap-1.5">
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-7 px-2 text-xs"
                                        onClick={() => setDraft({ from: dateRange.from, to: dateRange.to })}
                                    >
                                        {t('analytics.filters.reset')}
                                    </Button>
                                    <Button
                                        size="sm"
                                        className="h-7 px-3 text-xs"
                                        disabled={
                                            !draft.from ||
                                            !draft.to ||
                                            rangeDays({ from: draft.from, to: draft.to }) > MAX_ANALYTICS_RANGE_DAYS
                                        }
                                        onClick={handleApply}
                                    >
                                        {t('analytics.filters.apply')}
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </div>
                </PopoverContent>
            </Popover>

            {/* Granularity Selector - only shown for analytics (not sessions) */}
            {showGranularity && (
                <Select value={granularity} onValueChange={(v) => onGranularityChange(v as Granularity)}>
                    <SelectPrimitive.Trigger asChild>
                        <Button
                            variant="outline"
                            size="sm"
                            className="h-8 px-2.5 text-[11px] font-semibold"
                        >
                            <Clock className="mr-1.5 h-3 w-3 text-muted-foreground" />
                            <SelectValue />
                            <ChevronDown className="ml-1 h-3 w-3 opacity-50" />
                        </Button>
                    </SelectPrimitive.Trigger>
                    <SelectContent>
                        {availableGranularities.map((g) => (
                            <SelectItem key={g} value={g} className="text-xs">
                                {GRANULARITY_LABELS[g]}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            )}
        </div>
    );
}
